<template>
  <div>
    <PageHeader title="Data sources" subtitle="Every source behind Growth and the identifiers it joins on." :heading-level="1" />
    <LoadingIndicator v-if="loading" message="Loading data sources..." />
    <div v-else-if="d" class="px-6 py-4">
      <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table class="w-full text-[13px]">
          <thead>
            <tr class="text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
              <th class="text-left font-semibold px-4 py-2">Source</th>
              <th class="text-left font-semibold px-3 py-2">Type</th>
              <th class="text-left font-semibold px-3 py-2">Contents</th>
              <th class="text-left font-semibold px-3 py-2">Joins on</th>
              <th class="text-right font-semibold px-3 py-2">Records</th>
              <th class="text-left font-semibold px-3 py-2">Data as of</th>
              <th class="text-left font-semibold px-4 py-2">Last loaded</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="r in rows" :key="r.key" class="align-top">
              <td class="px-4 py-2 whitespace-nowrap">
                <div class="flex items-center gap-2.5">
                  <span v-if="r.mark" class="w-6 h-6 rounded flex items-center justify-center text-white text-[11px] font-bold shrink-0" :style="{ background: r.mark.color }">
                    <font-awesome-icon v-if="r.mark.icon" :icon="r.mark.icon" class="text-[11px]" /><template v-else>{{ r.mark.letter }}</template>
                  </span>
                  <div>
                    <div class="font-medium text-gray-900">{{ r.name }}</div>
                    <div v-if="r.integration" class="flex items-center gap-1.5 text-xs text-gray-500">
                      <span class="w-1.5 h-1.5 rounded-full bg-green-dark" />{{ r.integration }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-3 py-2 text-gray-600 whitespace-nowrap">{{ r.kind }}</td>
              <td class="px-3 py-2 text-gray-700">{{ r.what }}</td>
              <td class="px-3 py-2 text-gray-700 whitespace-nowrap">{{ r.keys.join(', ') }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-900">{{ r.records == null ? '—' : r.records.toLocaleString() }}</td>
              <td class="px-3 py-2 text-gray-700 whitespace-nowrap">{{ fmtDate(r.as_of) }}</td>
              <td class="px-4 py-2 text-gray-700 whitespace-nowrap">{{ fmtDate(r.loaded) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCloud } from '@fortawesome/free-solid-svg-icons'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'

library.add(faCloud)

const KIND_ORDER = ['Customer', 'Licensed', 'CMS public', 'HRSA public', 'Derived', 'Public']

export default {
  name: 'DataSourcesTab',
  components: { FontAwesomeIcon, PageHeader, LoadingIndicator },
  data() {
    return { d: null, loading: false }
  },
  computed: {
    rows() {
      const out = []
      const sf = this.d.salesforce
      if (sf) {
        out.push({
          key: 'salesforce', name: 'Salesforce', kind: 'Customer',
          what: `Accounts, leads, Events and Tasks. ${sf.accounts} accounts, ${sf.leads} leads, ${sf.engagements} engagements, ${sf.transcripts} with transcripts.`,
          keys: ['TIN', ...(sf.npi_field ? ['NPI'] : []), 'Account ID'], records: sf.accounts + sf.leads + sf.engagements,
          as_of: sf.last_sync_at, loaded: sf.last_sync_at,
          mark: { color: '#00A1E0', icon: ['fas', 'cloud'] }, integration: `Connected · syncs nightly`,
        })
      }
      const a = this.d.actuarial
      if (a) {
        out.push({
          key: 'actuarial', name: `${a.vendor} actuarial benchmarks`, kind: 'Customer',
          what: `Benchmark and regional benchmark by TIN and CCN, ${a.first_year}–${a.last_year}, with year-on-year headwind or tailwind. ${a.tins} TINs, ${a.ccns} CCNs, ${a.matched} linked.`,
          keys: ['TIN', 'CCN'], records: a.rows, as_of: a.delivered_at, loaded: a.loaded_at,
          mark: { color: '#1F3A5F', letter: 'M' }, integration: 'Connected · quarterly SFTP delivery',
        })
      }
      out.push({
        key: 'network', name: 'Network roster', kind: 'Customer',
        what: `Practices and TINs already in the network, excluded from Targets. ${this.d.network.practices.toLocaleString()} practices.`,
        keys: ['TIN'], records: this.d.network.tins, as_of: null, loaded: null,
      })
      for (const s of this.d.graph.sources) {
        out.push({ key: s.key, name: s.name, kind: s.kind, what: s.what, keys: s.keys, records: s.records, as_of: s.data_as_of, loaded: s.loaded_at })
      }
      return out.sort((x, y) => KIND_ORDER.indexOf(x.kind) - KIND_ORDER.indexOf(y.kind) || (x.kind === 'Customer' ? 0 : (y.records || 0) - (x.records || 0)))
    },
  },
  async created() {
    this.loading = true
    try {
      const { data } = await this.$axios.get('/api/growth/data-sources/')
      this.d = data
    } finally {
      this.loading = false
    }
  },
  methods: {
    fmtDate(v) {
      if (!v) return '—'
      const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
  },
}
</script>
